import { createContext, use, useRef } from "react";
import { createStore, useStore } from "zustand";

type TaskSelectionActions = {
    setSelectionByIds: (selectedTaskIds: Record<number, boolean>) => void;
    clearSelection: () => void;
    setSelection: (fn: (prev: Record<number, boolean>) => Record<number, boolean>) => void;
    setEditingTaskId: (id: number | null) => void;
};

type TaskSelectionState = {
    selectedTaskIds: Record<number, boolean>;
    editingTaskId: number | null;
    /** Grouped so consumers can subscribe to the actions without subscribing to state. */
    actions: TaskSelectionActions;
};

function createTaskSelectionStore() {
    return createStore<TaskSelectionState>((set) => ({
        selectedTaskIds: {},
        editingTaskId: null,
        actions: {
            setSelectionByIds: (selectedTaskIds: Record<number, boolean>) => set({ selectedTaskIds }),
            clearSelection: () => set({ selectedTaskIds: {} }),
            setSelection: (fn: (prev: Record<number, boolean>) => Record<number, boolean>) => {
                set((state) => ({ selectedTaskIds: fn(state.selectedTaskIds) }));
            },
            setEditingTaskId: (id: number | null) => set({ editingTaskId: id }),
        },
    }));
}

type TaskSelectionStore = ReturnType<typeof createTaskSelectionStore>;

export const TaskSelectionContext = createContext<TaskSelectionStore | null>(null);

export function TaskSelectionProvider({ children }: { children: React.ReactNode }) {
    const storeRef = useRef<TaskSelectionStore>(null);
    if (storeRef.current === null) {
        storeRef.current = createTaskSelectionStore();
    }
    return <TaskSelectionContext value={storeRef.current}>{children}</TaskSelectionContext>;
}

/**
 * The store itself, for imperative reads (`store.getState()`) from event handlers
 * that shouldn't re-render when the selection changes.
 */
export function useTaskSelectionStoreApi(): TaskSelectionStore {
    const store = use(TaskSelectionContext);
    if (!store) {
        throw new Error("useTaskSelectionStoreApi must be used within a TaskSelectionProvider");
    }
    return store;
}

/**
 * The action functions. Stable for the lifetime of the provider, so subscribing to
 * them never causes a re-render — unlike subscribing to the store as a whole,
 * which re-renders on every selection change.
 */
export function useTaskSelectionActions(): TaskSelectionActions {
    return useStore(useTaskSelectionStoreApi(), (state) => state.actions);
}

/**
 * Subscribes a single row to its own selected state. Selecting a row then
 * re-renders the row that gained selection and the one that lost it, instead of
 * every mounted row.
 */
export function useIsTaskSelected(taskId: number): boolean {
    return useStore(useTaskSelectionStoreApi(), (state) => !!state.selectedTaskIds[taskId]);
}

/** Subscribes a single title cell to whether it is the one being edited. */
export function useIsEditingTask(taskId: number): boolean {
    return useStore(useTaskSelectionStoreApi(), (state) => state.editingTaskId === taskId);
}
